import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { backgroundColor, roundOneDecimal } from "src/store/common";
import { alertMessages, herbFMColors, hunDeadFuelColors, noColors, oneDeadFuelColors, tenDeadFuelColors, thouDeadFuelColors, woodyFMColors, gsiColors, kbdiColors } from 'src/constants';
import { useStoreContext } from "src/store/context";
import { useAddAlert } from "src/store/actions";
import moment from "moment-timezone";
import { getNfdrHourly } from "src/apis/getNfdrsCalls";

type NfdrHourlyProps = {
    station: any,
    fuelModel: string,
    startDate: any,
    endDate: any,
}


export const NfdrHourly: React.FC<NfdrHourlyProps> = ({ station, fuelModel, startDate,endDate}) =>{
    const [state, dispatch] = useStoreContext();
    const [nfdrHourlyData, setNfdrHourlyData] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const addAlert = useAddAlert(dispatch);
    const [nfdrsHourlyFailCount, setNfdrsHourlyFailCount] = useState<number>(0);

    /**
     * Callback that will be called upon data initialization
     */
    const nfdrHourlyCallback = useCallback((counter:number) => {
    (async () => {
      try {
        const request = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          startHour: 0,
          endHour: 23,
          dateTimeFormat: 'LocalStationTime',
          stationIds: station.station_id?.toString(),
          fuelModel: fuelModel,
          sortBy: 'observation_time',
          sortOrder: 'DESC',
          page: 0,
          perPage: 25
        }

        const response: any = await getNfdrHourly(request, addAlert, state?.user);
        if(response.data){
          setNfdrHourlyData(response.data);
          setNfdrsHourlyFailCount(0);
          if((response._metadata.page ===0 && response._metadata.page_count ===1)||(response._metadata.page=== response._metadata.page_count)){
            setHasMore(false)
          }
          else{
            setHasMore(true)
          }
        }
      } catch (error) {
        console.log('error', error);
        setNfdrsHourlyFailCount(counter+1)
        setHasMore(false);
      }
    })();
  },[startDate, endDate, station, fuelModel, addAlert, state?.user, setNfdrsHourlyFailCount]);

  const fetchMoreData = useCallback(() =>{
    (async () => {
      try {
        const request = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          startHour: 0,
          endHour: 23,
          dateTimeFormat: 'LocalStationTime',
          stationIds: station.station_id?.toString(),
          fuelModel: fuelModel,
          sortBy: 'observation_time',
          sortOrder: 'DESC',
          page: page,
          perPage: 25
        }

        const response: any = await getNfdrHourly(request, addAlert);
        
        setNfdrHourlyData((prevData) => [...prevData, ...response.data]);
        response.data.length > 0 ? setHasMore(true) : setHasMore(false);
        setPage(page + 1);
      } catch (error) {
        console.log('error', error);
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  },[startDate, endDate, station, fuelModel, page, addAlert]);

  /**
   * Initialize the dataset when modifying the parameters
   */

  useEffect(() => {
    if(station){
      setPage(1);
      setHasMore(true);
      nfdrHourlyCallback(nfdrsHourlyFailCount);
    }
    if(nfdrsHourlyFailCount>0 && nfdrsHourlyFailCount<4 && station){
      nfdrHourlyCallback(nfdrsHourlyFailCount);
    }
  },[startDate, endDate, station, fuelModel, nfdrHourlyCallback, nfdrsHourlyFailCount]);
  
  return (
    <Box>
        <Paper sx ={{width: '99%'}}>
          <TableContainer className="table smooth-scrollbar" id="scrollableTable">
              <TableHead className="tableHead"                                
                style={{
                  position: "sticky",
                  top: 0,
                  display: 'flex',
                  zIndex: 110,
                  backgroundColor: "#A1A1A1",
                }}>
              <TableRow >
                <TableCell style={{width: "12%"}}>Date: HR (LST)</TableCell>
                <TableCell >1 hr FM</TableCell>
                <TableCell>10 hr FM</TableCell>
                <TableCell>100 hr FM</TableCell>
                <TableCell>1000 hr FM</TableCell>
                <TableCell>KBDI</TableCell>
                <TableCell>Herb FM</TableCell>
                <TableCell>Woody FM</TableCell>
                <TableCell>GSI</TableCell>
                <TableCell>IC</TableCell>
                <TableCell>SC</TableCell>
                <TableCell>ERC</TableCell>
                <TableCell>BI</TableCell>
              </TableRow>
              </TableHead>
              {station?.nfdrs_hourly_visible===false &&
                <TableBody>
                  <div style={{padding: '15px'}}>
                    Due to restrictions in place by the network owner, <span style={{ fontWeight: '700' }}>no hourly</span> data is available for this <span style={{ fontWeight: '700' }}>restricted network</span>.
                  </div>
                </TableBody>
              }
              {!hasMore && nfdrHourlyData?.length===0? (
                station?.nfdrs_hourly_visible &&
                <TableBody>
                  <div style={{padding: '15px'}}>
                    No Data Available for this Station for the Selected Date Range and Fuel Model
                  </div>
                </TableBody>
              ):(
                station?.nfdrs_hourly_visible &&
                <InfiniteScroll
                dataLength={nfdrHourlyData?.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={<p>Loading...</p>}
                scrollableTarget="scrollableTable"
              >
              <TableBody>
                {station?.nfdrs_hourly_visible && nfdrHourlyData?.map((row, index) => (
                  <TableRow
                    key={`${row.observation_time_lst} ${index}`}
                    style={{
                      width: '100%',
                      border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                    }}
                  >
                    <TableCell
                      style={{
                        backgroundColor: "rgb(175 158 158 / 87%)",
                        position: "sticky",
                        left: 0,
                        zIndex: 100,
                        width: "12%"
                      }}
                    >
                      {`${moment(row.display_hour_lst).utcOffset(station.time_zone_offset *60).format('MM-DD-YYYY HH:mm')} (${station.time_zone})`}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.one_hr_tl_fuel_moisture, oneDeadFuelColors).bg,
                        color: backgroundColor(row.one_hr_tl_fuel_moisture, oneDeadFuelColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                      }}
                    >
                      {roundOneDecimal(row.one_hr_tl_fuel_moisture)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.ten_hr_tl_fuel_moisture, tenDeadFuelColors).bg,
                        color: backgroundColor(row.ten_hr_tl_fuel_moisture, tenDeadFuelColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: 'black',

                      }}
                    >
                      {roundOneDecimal(row.ten_hr_tl_fuel_moisture)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.hun_hr_tl_fuel_moisture, hunDeadFuelColors).bg,
                        color: backgroundColor(row.hun_hr_tl_fuel_moisture, hunDeadFuelColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
                      }}
                    >
                      {roundOneDecimal(row.hun_hr_tl_fuel_moisture)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.thou_hr_tl_fuel_moisture, thouDeadFuelColors).bg,
                        color: backgroundColor(row.thou_hr_tl_fuel_moisture, thouDeadFuelColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',

                      }}
                    >
                      {roundOneDecimal(row.thou_hr_tl_fuel_moisture)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.kbdi, kbdiColors).bg,
                        color: backgroundColor(row.kbdi, kbdiColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',

                      }}
                    >
                      {roundOneDecimal(row.kbdi)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.herbaceous_lfi_fuel_moisture, herbFMColors).bg,
                        color: backgroundColor(row.herbaceous_lfi_fuel_moisture, herbFMColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',

                      }}
                    >
                      {roundOneDecimal(row.herbaceous_lfi_fuel_moisture)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.woody_lfi_fuel_moisture, woodyFMColors).bg,
                        color: backgroundColor(row.woody_lfi_fuel_moisture, woodyFMColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {roundOneDecimal(row.woody_lfi_fuel_moisture)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.gsi, gsiColors).bg,
                        color: backgroundColor(row.gsi, gsiColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {(row.gsi)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.ignition_component, noColors).bg,
                        color: backgroundColor(row.ignition_component, noColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {roundOneDecimal(row.ignition_component)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.spread_component, noColors).bg,
                        color: backgroundColor(row.spread_component, noColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {roundOneDecimal(row.spread_component)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.energy_release_component, noColors).bg,
                        color: backgroundColor(row.energy_release_component, noColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {roundOneDecimal(row.energy_release_component)}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.burning_index, noColors).bg,
                        color: backgroundColor(row.burning_index, noColors).text,
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {roundOneDecimal(row.burning_index)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </InfiniteScroll>
              )}
          </TableContainer>
        </Paper>    
    </Box>
  );
}
